import React, { useCallback, useEffect, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { CenterPlayButton, CenterReloadButton, Controls, ProgressBar } from '../components';
import type { SkinConfig, SkinParams } from './skinTypes';
import type { PlayerEventName, VideoPlayerInstance } from './types';
import styles from './CustomSkin.module.css';

/**
 * React-компонент скина
 */
interface SkinProps {
    player: VideoPlayerInstance;
    config: SkinConfig;
}

interface PlayerState {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isMuted: boolean;
    isEnded: boolean;
}

function SkinComponent({ player, config }: SkinProps) {
    const [playerState, setPlayerState] = useState<PlayerState>({
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        volume: 1,
        isMuted: false,
        isEnded: false,
    });
    const [isHovered, setIsHovered] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    // Хелпер для обновления состояния плеера
    const updatePlayerState = useCallback((updates: Partial<PlayerState>) => {
        setPlayerState((prev) => {
            return { ...prev, ...updates };
        });
    }, []);

    // Определение состояния окончания видео
    const determineEndedState = useCallback(
        (
            state: { playingState?: string; currentTime?: number | null; duration?: number | null },
            prevState: PlayerState
        ): boolean => {
            const isEndedByState = state.playingState === 'end' || state.playingState === 'ended';

            // Для зацикленного видео не показываем состояние "закончено"
            if (config.loop) {
                return false;
            }

            if (isEndedByState || prevState.isEnded) {
                return true;
            }

            // Проверка перехода с конца в начало (только для не зацикленного видео)
            const duration = state.duration || 0;

            if (duration > 0) {
                const wasNearEnd = prevState.currentTime > duration - 1;
                const nowAtStart = (state.currentTime || 0) < 1;

                return wasNearEnd && nowAtStart;
            }

            return false;
        },
        [config.loop]
    );

    // Обновление состояния плеера
    useEffect(() => {
        const updateState = () => {
            const state = player.getState();

            if (!state) {
                return;
            }

            const currentTime = state.currentTime || 0;
            const duration = state.duration || 0;
            const isPlaying = state.playingState === 'play';

            setPlayerState((prev) => {
                const isEnded = determineEndedState(state, prev);
                const actualIsPlaying = isEnded ? false : isPlaying;

                return {
                    ...prev,
                    isPlaying: actualIsPlaying,
                    currentTime,
                    duration,
                    volume: state.volume ?? prev.volume,
                    isMuted: state.muted ?? prev.isMuted,
                    isEnded,
                };
            });
        };

        // Обработчик окончания видео
        const handleEnded = () => {
            updatePlayerState({ isEnded: true, isPlaying: false });

            if (config.loop) {
                setTimeout(() => {
                    player.seek(0, 'loop-restart');
                    player.play('loop-restart');
                    updatePlayerState({ isEnded: false });
                }, 100);
            }
        };

        // Подписка на события
        const events: PlayerEventName[] = [
            'Play',
            'Pause',
            'VolumeChange',
            'DurationChange',
            'Seeking',
            'Seeked',
            'CurrentTimeChange',
        ];

        events.forEach((event) => player.on(event, updateState));
        player.on('Ended', handleEnded);
        updateState();

        return () => {
            events.forEach((event) => player.off(event, updateState));
            player.off('Ended', handleEnded);
        };
    }, [player, config.loop, determineEndedState, updatePlayerState]);

    // Обработчики событий
    const handlePlayPause = useCallback(() => {
        if (playerState.isEnded) {
            // Перезапуск после окончания
            updatePlayerState({ isEnded: false, isPlaying: true });
            player.seek(0, 'react-skin-play-restart');
            player.play('react-skin-play-restart');
        } else {
            // Обычное переключение play/pause
            const newIsPlaying = !playerState.isPlaying;

            updatePlayerState({ isPlaying: newIsPlaying });

            if (newIsPlaying) {
                player.play('react-skin-control');
            } else {
                player.pause('react-skin-control');
            }
        }
    }, [player, playerState.isPlaying, playerState.isEnded, updatePlayerState]);

    const handleSeek = useCallback(
        (time: number) => {
            updatePlayerState({ currentTime: time });
            player.seek(time, 'react-skin-seek');
        },
        [player, updatePlayerState]
    );

    const handleVolumeChange = useCallback(
        (volume: number) => {
            updatePlayerState({ volume });
            player.setVolume(volume);
        },
        [player, updatePlayerState]
    );

    const handleMuteToggle = useCallback(() => {
        const newIsMuted = !playerState.isMuted;

        updatePlayerState({ isMuted: newIsMuted });
        player.setMuted(newIsMuted);
    }, [player, playerState.isMuted, updatePlayerState]);

    const handleFullscreen = useCallback(() => {
        player.requestVideoElementFullscreen();
    }, [player]);

    const handleReload = useCallback(() => {
        updatePlayerState({ isEnded: false });
        player.seek(0, 'react-skin-reload');
        player.play('react-skin-reload');
    }, [player, updatePlayerState]);

    // Проверка скрытых контролов
    const isControlHidden = useCallback(
        (controlName: string): boolean => {
            if (!config.hiddenControls) {
                return false;
            }

            if (config.hiddenControls === '*') {
                return true;
            }

            return config.hiddenControls.includes(controlName);
        },
        [config.hiddenControls]
    );

    // Определение видимости элементов
    const shouldShowProgressBar = config.showProgressBar !== false && !isControlHidden('progressbar');
    const shouldShowControls = config.showControls !== false && !isControlHidden('*');
    const shouldShowOverlay = shouldShowProgressBar || shouldShowControls;

    // Обработчик взаимодействия пользователя
    const handleUserInteraction = useCallback(() => {
        if (config.autoplay && !hasUserInteracted) {
            setHasUserInteracted(true);
        }
    }, [config.autoplay, hasUserInteracted]);

    // Обработчик клика по плееру
    const handlePlayerClick = useCallback(
        (event: React.MouseEvent) => {
            const target = event.target as HTMLElement;
            const isControlClick = target.closest(`.${styles.progressContainer}, .${styles.controlsContainer}`);

            if (!isControlClick) {
                handlePlayPause();
            }
        },
        [handlePlayPause]
    );

    // Комбинированные обработчики событий
    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
        handleUserInteraction();
    }, [handleUserInteraction]);

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            handleUserInteraction();
            handlePlayerClick(event);
        },
        [handleUserInteraction, handlePlayerClick]
    );

    const handleTouchStart = useCallback(() => {
        handleUserInteraction();
    }, [handleUserInteraction]);

    // Если overlay не нужен, возвращаем пустой div
    if (!shouldShowOverlay) {
        return <div className={styles.customVideoSkin} />;
    }

    // Определение видимости контролов
    const shouldShowControlsVisually = config.autoplay ? hasUserInteracted && isHovered : isHovered;

    return (
        <div
            className={styles.customVideoSkin}
            data-autoplay={config.autoplay || false}
            data-no-interaction={config.autoplay && !hasUserInteracted}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick}
            onTouchStart={handleTouchStart}
        >
            <div className={`${styles.skinOverlay} ${isHovered ? styles.hovered : ''}`}>
                {shouldShowProgressBar && (
                    <div
                        className={`${styles.progressContainer} ${shouldShowControlsVisually ? styles.visible : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ProgressBar
                            currentTime={playerState.currentTime}
                            duration={playerState.duration}
                            onSeek={handleSeek}
                        />
                    </div>
                )}

                {shouldShowControls && (
                    <div
                        className={`${styles.controlsContainer} ${shouldShowControlsVisually ? styles.visible : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Controls
                            isPlaying={playerState.isPlaying}
                            currentTime={playerState.currentTime}
                            duration={playerState.duration}
                            volume={playerState.volume}
                            isMuted={playerState.isMuted}
                            onPlayPause={handlePlayPause}
                            onVolumeChange={handleVolumeChange}
                            onMuteToggle={handleMuteToggle}
                            onFullscreen={handleFullscreen}
                            hiddenControls={config.hiddenControls}
                        />
                    </div>
                )}

                <CenterPlayButton
                    isVisible={!playerState.isPlaying && !playerState.isEnded}
                    onClick={handlePlayPause}
                />

                <CenterReloadButton visible={playerState.isEnded} onClick={handleReload} />
            </div>
        </div>
    );
}

/**
 * React скин для видеоплеера
 */
export class CustomSkin {
    private config: SkinConfig;
    private player: VideoPlayerInstance;
    private container: HTMLElement;
    private skinElement: HTMLElement | null = null;
    private reactRoot: Root | null = null;
    private isDestroyed = false;

    constructor(params: SkinParams) {
        this.container = params.container;
        this.player = params.player;
        this.config = {
            showProgressBar: true,
            showControls: true,
            ...params.skinConfig,
        };

        this.init();
    }

    private init(): void {
        this.createSkinElement();
    }

    private createSkinElement(): void {
        this.skinElement = document.createElement('div');
        this.skinElement.className = 'video-player-skin-container';
        this.skinElement.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
        `;

        this.container.appendChild(this.skinElement);
        this.reactRoot = createRoot(this.skinElement);
        this.renderSkin();
    }

    private renderSkin(): void {
        if (!this.reactRoot || this.isDestroyed) {
            return;
        }

        this.reactRoot.render(<SkinComponent player={this.player} config={this.config} />);
    }

    public configure(config: Partial<SkinConfig>): void {
        if (this.isDestroyed) {
            return;
        }

        this.config = { ...this.config, ...config };
        this.renderSkin();
    }

    public destroy(): void {
        if (this.isDestroyed) {
            return;
        }

        this.isDestroyed = true;

        if (this.reactRoot) {
            this.reactRoot.unmount();
            this.reactRoot = null;
        }

        if (this.skinElement) {
            this.skinElement.remove();
            this.skinElement = null;
        }
    }

    public getElement(): HTMLElement | null {
        return this.skinElement;
    }
}

/**
 * Фабрика для создания React скина
 */
export function createCustomSkin(params: SkinParams): Promise<CustomSkin> {
    return Promise.resolve(new CustomSkin(params));
}
